// src/components/student/AddStudentLayer.jsx

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { FaTrash, FaPlus, FaCopy, FaCheckCircle, FaExclamationTriangle, FaCamera } from "react-icons/fa";

import { getClasses } from "../../services/classService";
import {
  getStudents,
  addStudents,
  updateStudents,
} from "../../services/studentService";
import "../teacher/TeacherLayer.css";

const SuccessToast = ({ message, email, password }) => (
  <div>
    <div className="fw-bold mb-1">{message}</div>
    {email && (
      <div className="d-flex align-items-center justify-content-between bg-white p-2 rounded border mt-2">
        <div style={{ fontSize: '13px', color: '#333' }}>
          <div><strong>Email:</strong> {email}</div>
          <div><strong>Pass:</strong> {password}</div>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-light border d-flex align-items-center gap-1 ms-2"
          onClick={() => {
            navigator.clipboard.writeText(`Email: ${email}\nPassword: ${password}`);
            toast.success("Copied to clipboard!");
          }}
        >
          <FaCopy /> Copy
        </button>
      </div>
    )}
  </div>
);

const ErrorToast = ({ message }) => {
  const displayMsg = typeof message === "string" 
    ? message 
    : (message && typeof message === "object" ? Object.values(message).flat().join(", ") : JSON.stringify(message) || "Something went wrong");
    
  return (
    <div className="d-flex align-start gap-2">
      <div className="text-danger mt-1 fs-5">
        <FaExclamationTriangle />
      </div>
      <div>
        <div className="fw-bold text-danger mb-1">Action Failed</div>
        <div style={{ fontSize: '13.5px', color: '#444' }}>{displayMsg}</div>
      </div>
    </div>
  );
};

const StudentLayer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  
  const [showGuardian, setShowGuardian] = useState(false);

  // Common family & contact details
  const [familyDetails, setFamilyDetails] = useState({
    father_name: "",
    mother_name: "",
    guardian_name: "",
    email: "",
    password: "",
    mobile: "",
    status: "active",
  });

  // Array of students to be added / updated
  const [studentsList, setStudentsList] = useState([
    {
      name: "",
      roll_number: "",
      class_id: "",
      birthdate: "",
      gender: "Male",
      id_card_code: "",
      reg_number: "",
      blood_group: "",
      section: "A",
      academic_year: "2024-2025",
      address: "",
      student_photo: "",
    },
  ]);

  useEffect(() => {
    loadClasses();

    if (isEdit) {
      loadStudent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadClasses = async () => {
    try {
      const res = await getClasses();

      if (res?.status) {
        setClasses(res.data || []);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const loadStudent = async () => {
    try {
      const res = await getStudents();

      if (res?.status) {
        const student = (res.data || []).find(
          (item) => String(item.id) === String(id)
        );

        if (student) {
          const isGuardianMode = Boolean(student.guardian_name) && !student.father_name && !student.mother_name;
          setShowGuardian(isGuardianMode);

          setFamilyDetails({
            father_name: student.father_name || "",
            mother_name: student.mother_name || "",
            guardian_name: student.guardian_name || "",
            email: student.email || "",
            password: student.plain_password || student.password || "",
            mobile: student.mobile || "",
            status: student.status || "active",
          });

          setStudentsList([
            {
              name: student.name || "",
              roll_number: student.roll_number || "",
              class_id: student.class_id || "",
              birthdate: student.birthdate || "",
              gender: student.gender || "Male",
              id_card_code: student.id_card_code || student.id_card_number || "",
              reg_number: student.reg_number || "",
              blood_group: student.blood_group || "",
              section: student.section || "A",
              academic_year: student.academic_year || "2024-2025",
              address: student.address || "",
              student_photo: student.student_photo || "",
            },
          ]);
        }
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleFamilyChange = (e) => {
    const { name, value } = e.target;
    setFamilyDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleStudentChange = (index, e) => {
    const { name, value } = e.target;
    const updatedList = [...studentsList];
    updatedList[index][name] = value;
    setStudentsList(updatedList);
  };

  const addMoreStudent = () => {
    setStudentsList([
      ...studentsList,
      {
        name: "",
        roll_number: "",
        class_id: studentsList[0]?.class_id || "",
        birthdate: "",
        gender: "Male",
        id_card_code: "",
        reg_number: "",
        blood_group: "",
        section: studentsList[0]?.section || "A",
        academic_year: studentsList[0]?.academic_year || "2024-2025",
        address: "",
        student_photo: "",
      },
    ]);
  };

  const removeStudent = (index) => {
    const updatedList = studentsList.filter((_, i) => i !== index);
    setStudentsList(updatedList);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!familyDetails.mobile) {
      toast.error("Mobile Number is required");
      return;
    }

    if (familyDetails.mobile && familyDetails.mobile.length !== 10) {
      toast.error("Mobile Number must be exactly 10 digits");
      return;
    }

    for (let i = 0; i < studentsList.length; i++) {
      if (!studentsList[i].name) {
        toast.error(`Student #${i + 1} Name is required`);
        return;
      }
      if (!studentsList[i].id_card_code) {
        toast.error(`Student #${i + 1} ID Card Number is required`);
        return;
      }
    }

    setLoading(true);

    try {
      let payload;

      if (isEdit) {
        payload = { id, ...familyDetails, ...studentsList[0] };
      } else {
        const constructedStudents = studentsList.map((stu, idx) => {
          if (idx === 0) {
            return { ...stu, ...familyDetails };
          }
          return {
            ...stu,
            mobile: familyDetails.mobile,
            email: familyDetails.email,
            password: familyDetails.password,
            father_name: familyDetails.father_name,
            mother_name: familyDetails.mother_name,
            guardian_name: familyDetails.guardian_name,
            status: familyDetails.status,
          };
        });
        payload = { students: constructedStudents };
      }

      const res = isEdit
        ? await updateStudents(payload)
        : await addStudents(payload);

      const isSuccess = res?.status === true || res?.status === "true" || res?.status === 1 || res?.status === "1";

      if (isSuccess) {
        const successMsg = res?.message || (isEdit ? "Student Updated Successfully" : "Students Added Successfully");
        
        toast.success(
          <SuccessToast 
            message={successMsg} 
            email={familyDetails.email} 
            password={familyDetails.password} 
          />,
          { autoClose: 6000 }
        );

        setTimeout(() => {
          navigate("/student-page-list");
        }, 1500);
      } else {
        const message = res?.message || "Something went wrong";
        toast.error(<ErrorToast message={message} />, { icon: false });
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error.message || "Server Error";
      toast.error(<ErrorToast message={errorMsg} />, { icon: false });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="school-wrapper">
      <div className="school-card smooth-card">
        <div className="admin-page-shell">
          <div className="admin-page-header list-header mb-4">
            <div>
              <h4 className="fw-bold text-dark mb-1">
                {isEdit ? "Update Student Profile" : "Enroll New Student"}
              </h4>
              <p className="text-secondary m-0" style={{ fontSize: "14px" }}>
                Fill in official details to register/update the student in the system.
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            
            {/* Family & Contact Details Card */}
            <div className="card border-0 shadow-sm rounded-4 mb-3">
              <div className="card-header bg-white border-bottom-0 pt-4 pb-0 px-4 d-flex flex-wrap justify-content-between align-items-center gap-3">
                <h6 className="fw-bold text-primary mb-0 d-flex align-items-center gap-2 text-uppercase" style={{ letterSpacing: '0.5px', fontSize: '15px' }}>
                  <div style={{ width: '6px', height: '18px', backgroundColor: '#4f46e5', borderRadius: '3px' }}></div>
                  Family & Contact Details
                </h6>
                <button
                  type="button"
                  className={`btn btn-sm d-flex align-items-center gap-2 px-3 py-1 rounded-pill fw-semibold shadow-sm transition-all ${showGuardian ? 'btn-primary bg-primary border-0 text-white' : 'btn-light border border-secondary text-secondary'}`}
                  onClick={() => {
                    const newVal = !showGuardian;
                    setShowGuardian(newVal);
                    if (newVal) {
                      setFamilyDetails(prev => ({...prev, father_name: "", mother_name: ""}));
                    } else {
                      setFamilyDetails(prev => ({...prev, guardian_name: ""}));
                    }
                  }}
                  style={{ fontSize: '13px' }}
                >
                  {showGuardian ? <FaCheckCircle /> : <FaPlus />}
                  {showGuardian ? "Using Guardian Details" : "Provide Guardian Details"}
                </button>
              </div>
              <div className="card-body p-4 pt-3">
                <div className="row g-3">

                  {!showGuardian ? (
                    <>
                      <div className="col-lg-6 col-md-6">
                        <label className="form-label fw-semibold text-secondary" style={{ fontSize: '13px' }}>Father's Full Name</label>
                        <input
                          type="text"
                          name="father_name"
                          className="form-control form-control-lg bg-light border-0 shadow-none"
                          style={{ borderRadius: '10px', fontSize: '15px' }}
                          placeholder="Enter Father Name"
                          value={familyDetails.father_name}
                          onChange={handleFamilyChange}
                        />
                      </div>

                      <div className="col-lg-6 col-md-6">
                        <label className="form-label fw-semibold text-secondary" style={{ fontSize: '13px' }}>Mother's Full Name</label>
                        <input
                          type="text"
                          name="mother_name"
                          className="form-control form-control-lg bg-light border-0 shadow-none"
                          style={{ borderRadius: '10px', fontSize: '15px' }}
                          placeholder="Enter Mother Name"
                          value={familyDetails.mother_name}
                          onChange={handleFamilyChange}
                        />
                      </div>
                    </>
                  ) : (
                    <div className="col-12">
                      <label className="form-label fw-semibold text-secondary" style={{ fontSize: '13px' }}>Guardian's Full Name</label>
                      <input
                        type="text"
                        name="guardian_name"
                        className="form-control form-control-lg bg-light border-0 shadow-none"
                        style={{ borderRadius: '10px', fontSize: '15px' }}
                        placeholder="Enter Guardian Name"
                        value={familyDetails.guardian_name}
                        onChange={handleFamilyChange}
                      />
                    </div>
                  )}

                  <div className="col-lg-3 col-md-6">
                    <label className="form-label fw-semibold text-secondary" style={{ fontSize: '13px' }}>
                      Mobile Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      name="mobile"
                      className="form-control form-control-lg bg-light border-0 shadow-none"
                      style={{ borderRadius: '10px', fontSize: '15px' }}
                      placeholder="Enter Mobile Number (Required)"
                      value={familyDetails.mobile}
                      onChange={handleFamilyChange}
                      required
                    />
                  </div>

                  <div className="col-lg-3 col-md-6">
                    <label className="form-label fw-semibold text-secondary" style={{ fontSize: '13px' }}>Email Address</label>
                    <input
                      type="email"
                      name="email"
                      className="form-control form-control-lg bg-light border-0 shadow-none"
                      style={{ borderRadius: '10px', fontSize: '15px' }}
                      placeholder="Enter Email Address"
                      value={familyDetails.email}
                      onChange={handleFamilyChange}
                    />
                  </div>

                  <div className="col-lg-3 col-md-6">
                    <label className="form-label fw-semibold text-secondary" style={{ fontSize: '13px' }}>Account Password</label>
                    <input
                      type="text"
                      name="password"
                      className="form-control form-control-lg bg-light border-0 shadow-none"
                      style={{ borderRadius: '10px', fontSize: '15px' }}
                      placeholder="Enter Password"
                      value={familyDetails.password}
                      onChange={handleFamilyChange}
                    />
                  </div>

                  <div className="col-lg-3 col-md-6">
                    <label className="form-label fw-semibold text-secondary" style={{ fontSize: '13px' }}>Account Status</label>
                    <select
                      name="status"
                      className="form-select form-select-lg bg-light border-0 shadow-none"
                      style={{ borderRadius: '10px', fontSize: '15px' }}
                      value={familyDetails.status}
                      onChange={handleFamilyChange}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-3 mt-1">
              <h6 className="fw-bold text-primary m-0 d-flex align-items-center gap-2 text-uppercase" style={{ letterSpacing: '0.5px', fontSize: '15px' }}>
                <div style={{ width: '6px', height: '18px', backgroundColor: '#06b6d4', borderRadius: '3px' }}></div>
                Official Student Records
              </h6>
              {!isEdit && (
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center gap-2 px-4 py-2 rounded-pill shadow-sm text-white fw-semibold"
                  style={{ background: "linear-gradient(135deg, #4f46e5, #06b6d4)", border: "none", fontSize: '14px' }}
                  onClick={addMoreStudent}
                >
                  <FaPlus /> Add Sibling
                </button>
              )}
            </div>

            {studentsList.map((student, index) => (
              <div key={index} className="card border-0 shadow-sm rounded-4 mb-3">
                <div className="card-header bg-white border-bottom-0 pt-3 pb-1 px-4 d-flex justify-content-between align-items-center">
                  <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-1 rounded-pill fw-bold" style={{ fontSize: '13px' }}>
                    Student #{index + 1}
                  </span>
                  {index > 0 && !isEdit && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1 rounded-pill px-3"
                      onClick={() => removeStudent(index)}
                      style={{ fontSize: '13px' }}
                    >
                      <FaTrash size={12} /> Remove
                    </button>
                  )}
                </div>
                <div className="card-body px-4 pb-4 pt-2">
                  <div className="row g-3">
                    
                    <div className="col-lg-3 col-md-6">
                      <label className="form-label fw-semibold text-secondary" style={{ fontSize: '13px' }}>
                        Full Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        className="form-control form-control-lg bg-light border-0 shadow-none"
                        style={{ borderRadius: '10px', fontSize: '15px' }}
                        placeholder="Enter Student Name"
                        value={student.name}
                        onChange={(e) => handleStudentChange(index, e)}
                        required
                      />
                    </div>

                    <div className="col-lg-3 col-md-6">
                      <label className="form-label fw-semibold text-secondary" style={{ fontSize: '13px' }}>
                        ID Card Number <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="id_card_code"
                        className="form-control form-control-lg bg-light border-0 shadow-none"
                        style={{ borderRadius: '10px', fontSize: '15px' }}
                        placeholder="Enter ID Card Number (e.g. IDC-101)"
                        value={student.id_card_code}
                        onChange={(e) => handleStudentChange(index, e)}
                        required
                      />
                    </div>

                    <div className="col-lg-3 col-md-6">
                      <label className="form-label fw-semibold text-secondary" style={{ fontSize: '13px' }}>Roll Number</label>
                      <input
                        type="text"
                        name="roll_number"
                        className="form-control form-control-lg bg-light border-0 shadow-none"
                        style={{ borderRadius: '10px', fontSize: '15px' }}
                        placeholder="Enter Roll Number"
                        value={student.roll_number}
                        onChange={(e) => handleStudentChange(index, e)}
                      />
                    </div>

                    <div className="col-lg-3 col-md-6">
                      <label className="form-label fw-semibold text-secondary" style={{ fontSize: '13px' }}>Registration Number</label>
                      <input
                        type="text"
                        name="reg_number"
                        className="form-control form-control-lg bg-light border-0 shadow-none"
                        style={{ borderRadius: '10px', fontSize: '15px' }}
                        placeholder="Enter Reg Number (e.g. REG-101)"
                        value={student.reg_number}
                        onChange={(e) => handleStudentChange(index, e)}
                      />
                    </div>

                    <div className="col-lg-2 col-md-4">
                      <label className="form-label fw-semibold text-secondary" style={{ fontSize: '13px' }}>Class Assigned</label>
                      <select
                        name="class_id"
                        className="form-select form-select-lg bg-light border-0 shadow-none"
                        style={{ borderRadius: '10px', fontSize: '15px' }}
                        value={student.class_id}
                        onChange={(e) => handleStudentChange(index, e)}
                      >
                        <option value="">Select Class</option>
                        {classes.map((cls) => (
                          <option key={cls.id} value={cls.id}>
                            {cls.class_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-lg-2 col-md-4">
                      <label className="form-label fw-semibold text-secondary" style={{ fontSize: '13px' }}>Section</label>
                      <input
                        type="text"
                        name="section"
                        className="form-control form-control-lg bg-light border-0 shadow-none"
                        style={{ borderRadius: '10px', fontSize: '15px' }}
                        placeholder="Section (e.g. A)"
                        value={student.section}
                        onChange={(e) => handleStudentChange(index, e)}
                      />
                    </div>

                    <div className="col-lg-2 col-md-4">
                      <label className="form-label fw-semibold text-secondary" style={{ fontSize: '13px' }}>Gender</label>
                      <select
                        name="gender"
                        className="form-select form-select-lg bg-light border-0 shadow-none"
                        style={{ borderRadius: '10px', fontSize: '15px' }}
                        value={student.gender}
                        onChange={(e) => handleStudentChange(index, e)}
                      >
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div className="col-lg-2 col-md-4">
                      <label className="form-label fw-semibold text-secondary" style={{ fontSize: '13px' }}>Blood Group</label>
                      <select
                        name="blood_group"
                        className="form-select form-select-lg bg-light border-0 shadow-none"
                        style={{ borderRadius: '10px', fontSize: '15px' }}
                        value={student.blood_group}
                        onChange={(e) => handleStudentChange(index, e)}
                      >
                        <option value="">Select</option>
                        <option value="A+">A+</option>
                        <option value="A-">A-</option>
                        <option value="B+">B+</option>
                        <option value="B-">B-</option>
                        <option value="O+">O+</option>
                        <option value="O-">O-</option>
                        <option value="AB+">AB+</option>
                        <option value="AB-">AB-</option>
                      </select>
                    </div>

                    <div className="col-lg-2 col-md-4">
                      <label className="form-label fw-semibold text-secondary" style={{ fontSize: '13px' }}>Date of Birth</label>
                      <input
                        type="date"
                        name="birthdate"
                        value={student.birthdate}
                        onChange={(e) => handleStudentChange(index, e)}
                        className="form-control form-control-lg bg-light border-0 shadow-none"
                        style={{ borderRadius: '10px', fontSize: '15px' }}
                      />
                    </div>

                    <div className="col-lg-2 col-md-4">
                      <label className="form-label fw-semibold text-secondary" style={{ fontSize: '13px' }}>Academic Year</label>
                      <input
                        type="text"
                        name="academic_year"
                        value={student.academic_year}
                        onChange={(e) => handleStudentChange(index, e)}
                        className="form-control form-control-lg bg-light border-0 shadow-none"
                        style={{ borderRadius: '10px', fontSize: '15px' }}
                        placeholder="e.g. 2024-2025"
                      />
                    </div>

                    <div className="col-lg-6 col-md-6">
                      <label className="form-label fw-semibold text-secondary" style={{ fontSize: '13px' }}>Address</label>
                      <input
                        type="text"
                        name="address"
                        value={student.address}
                        onChange={(e) => handleStudentChange(index, e)}
                        className="form-control form-control-lg bg-light border-0 shadow-none"
                        style={{ borderRadius: '10px', fontSize: '15px' }}
                        placeholder="Enter full residential address"
                      />
                    </div>

                    {/* Student Photo Upload & File Picker */}
                    <div className="col-lg-6 col-md-6">
                      <label className="form-label fw-semibold text-secondary" style={{ fontSize: '13px' }}>
                        Student Photo Upload
                      </label>
                      <div className="d-flex align-items-center gap-3 bg-light p-2 rounded-3 border">
                        {student.student_photo ? (
                          <div className="position-relative" style={{ width: "48px", height: "48px", flexShrink: 0 }}>
                            <img 
                              src={student.student_photo.startsWith("data:") || student.student_photo.startsWith("http") ? student.student_photo : `/uploads/${student.student_photo}`} 
                              alt="Student Preview" 
                              className="rounded-circle border shadow-sm w-100 h-100 object-fit-cover"
                              onError={(e) => { e.target.src = "https://via.placeholder.com/48?text=Photo"; }}
                            />
                            <button
                              type="button"
                              className="btn btn-danger btn-sm p-0 rounded-circle position-absolute top-0 end-0 d-flex align-items-center justify-content-center"
                              style={{ width: "18px", height: "18px", fontSize: "11px", lineHeight: "1" }}
                              onClick={() => {
                                const updated = [...studentsList];
                                updated[index].student_photo = "";
                                setStudentsList(updated);
                              }}
                              title="Remove photo"
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <div className="bg-white border rounded-circle d-flex align-items-center justify-content-center text-muted shadow-sm" style={{ width: "48px", height: "48px", flexShrink: 0 }}>
                            <FaCamera fontSize="18px" className="text-primary" />
                          </div>
                        )}
                        
                        <div className="flex-grow-1">
                          <input
                            type="file"
                            accept="image/*"
                            className="form-control form-control-sm border bg-white shadow-none mb-1"
                            style={{ borderRadius: '6px', fontSize: '12px' }}
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  const updated = [...studentsList];
                                  updated[index].student_photo = reader.result;
                                  setStudentsList(updated);
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                          />
                          <input
                            type="text"
                            name="student_photo"
                            value={student.student_photo}
                            onChange={(e) => handleStudentChange(index, e)}
                            className="form-control form-control-sm border bg-white shadow-none"
                            style={{ borderRadius: '6px', fontSize: '12px' }}
                            placeholder="Or enter filename e.g. devav.png"
                          />
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            ))}

            <div className="d-flex flex-column flex-sm-row gap-3 mt-5 pb-5">
              <button
                type="submit"
                className="btn btn-primary btn-lg rounded-pill px-5 shadow text-white fw-bold d-flex align-items-center justify-content-center gap-2"
                disabled={loading}
                style={{ background: "linear-gradient(135deg, #4f46e5, #06b6d4)", border: "none" }}
              >
                <FaCheckCircle />
                {loading
                  ? "Please Wait..."
                  : isEdit
                  ? "Update Student"
                  : "Complete Registration"}
              </button>

              <button
                type="button"
                className="btn btn-light btn-lg rounded-pill px-5 fw-bold border"
                onClick={() => navigate("/student-page-list")}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StudentLayer;